import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Alert, Box, Snackbar, Typography } from '@mui/material'
import { salesPolicyService } from '../services'
import useAuthStore from '../store/authStore'
import NegativeSalesReportFilters from '../components/NegativeSalesReport/NegativeSalesReportFilters'
import NegativeSalesReportTable from '../components/NegativeSalesReport/NegativeSalesReportTable'
import { getDateRangeError } from '../components/NegativeSalesReport/dateValidation'
import { DEFAULT_ROWS_PER_PAGE } from '../components/NegativeSalesReport/constants'

/**
 * Super-admin Negative Sales cross-outlet report.
 * API: GET /admin/reports/negative-sales → { items, pagination }
 * Export: POST /admin/reports/negative-sales/export (Kafka job → Exports page)
 */
export default function NonInventorySalesReportPage() {
  const navigate = useNavigate()
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedOrgs, setSelectedOrgs] = useState([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)
  const [exporting, setExporting] = useState(false)
  const [exportAnchor, setExportAnchor] = useState(null)
  const [snackbar, setSnackbar] = useState(null)
  const requestIdRef = useRef(0)

  const dateRangeError = useMemo(
    () => getDateRangeError(from, to),
    [from, to],
  )

  /** Omitted entirely when nothing is picked, which the API reads as all outlets. */
  const selectedOrgIdsCsv = useMemo(
    () =>
      selectedOrgs.length > 0
        ? selectedOrgs.map((org) => org.id).join(',')
        : undefined,
    [selectedOrgs],
  )

  const queryParams = useMemo(() => {
    if (dateRangeError) return null
    return {
      from: from || undefined,
      to: to || undefined,
      organizationIds: selectedOrgIdsCsv,
      search: search.trim() || undefined,
      page: page + 1,
      limit: rowsPerPage,
    }
  }, [from, to, selectedOrgIdsCsv, search, page, rowsPerPage, dateRangeError])

  const load = useCallback(async () => {
    if (!queryParams) {
      setRows([])
      setTotal(0)
      setError('')
      setLoading(false)
      return
    }

    const requestId = ++requestIdRef.current
    setLoading(true)
    setError('')
    try {
      const data = await salesPolicyService.negativeSalesReport(queryParams)
      if (requestId !== requestIdRef.current) return
      setRows(Array.isArray(data?.items) ? data.items : [])
      setTotal(data?.pagination?.total ?? 0)
    } catch (e) {
      if (requestId !== requestIdRef.current) return
      setError(
        e?.response?.data?.message || e.message || 'Failed to load report',
      )
      setRows([])
      setTotal(0)
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [queryParams])

  useEffect(() => {
    if (!isSuperuserActor) return
    load()
  }, [isSuperuserActor, load])

  const resetPage = useCallback(() => {
    setPage(0)
  }, [])

  const handleOrgsChange = useCallback(
    (next) => {
      setSelectedOrgs(next)
      resetPage()
    },
    [resetPage],
  )

  const handleFromChange = useCallback(
    (value) => {
      setFrom(value)
      resetPage()
    },
    [resetPage],
  )

  const handleToChange = useCallback(
    (value) => {
      setTo(value)
      resetPage()
    },
    [resetPage],
  )

  const handleSearchApply = useCallback(
    (value) => {
      setSearch(String(value || '').trim())
      resetPage()
    },
    [resetPage],
  )

  const handleExport = useCallback(
    async (format) => {
      setExportAnchor(null)
      if (exporting || dateRangeError) return
      setExporting(true)
      try {
        const response = await salesPolicyService.createNegativeSalesExport({
          format,
          from: from || undefined,
          to: to || undefined,
          organizationIds: selectedOrgIdsCsv,
          search: search.trim() || undefined,
        })
        navigate('/administration/negative-sales/exports', {
          state: {
            queuedMessage:
              response?.message ||
              'Export started. It will appear in Negative Sales Exports when the file is ready.',
          },
        })
      } catch (e) {
        setSnackbar({
          severity: 'error',
          message:
            e?.response?.data?.message || e.message || 'Failed to start export',
        })
      } finally {
        setExporting(false)
      }
    },
    [
      exporting,
      dateRangeError,
      from,
      to,
      selectedOrgIdsCsv,
      search,
      navigate,
    ],
  )

  if (!isSuperuserActor) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Negative Sales Report
      </Typography>
      <NegativeSalesReportFilters
        selectedOrgs={selectedOrgs}
        onOrgsChange={handleOrgsChange}
        from={from}
        to={to}
        onFromChange={handleFromChange}
        onToChange={handleToChange}
        fromError={dateRangeError || ''}
        toError={
          from && to && from > to ? dateRangeError || '' : ''
        }
        search={search}
        onSearchApply={handleSearchApply}
        onRefresh={load}
        loading={loading}
        exporting={exporting}
        exportDisabled={exporting || Boolean(dateRangeError)}
        exportAnchor={exportAnchor}
        onExportOpen={(event) => setExportAnchor(event.currentTarget)}
        onExportClose={() => setExportAnchor(null)}
        onExport={handleExport}
        onViewExports={() =>
          navigate('/administration/negative-sales/exports')
        }
      />
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      <NegativeSalesReportTable
        rows={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10))
          setPage(0)
        }}
      />
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}
